/**
 *
 * @module playbackCleanup
 * @see https://devflow.jenkins.us-east-1.bamgrid.net/job/SDK-Doc/job/spec-sdk/job/master/1766/artifact/dist/uml/architecture/scenarios/sequence-playback.svg
 *
 */

import Check from 'check-types';
import assert from 'assert';

import SDK from './../../sdk';
import BaseSequence from './../base';
import PlaybackSequence from './playback';
import Exception from './../../services/exception/entry';

const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    assigned,
    object,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class PlaybackCleanupSequence
 * @extends {BaseSequence}
 * @desc The cleanup portion of the primary playback sequence. Executes the `PlaybackSequence` internally.
 * @todo Update sequence to v3
 *
 * @example const playbackCleanupSequence = new SDK.Test.Sequences.PlaybackCleanup(
 *     config, Html5PlayerAdapter, videoElement, queryContext, graphQlQuery, accountClaim
 * );
 *
 */
export default class PlaybackCleanupSequence extends BaseSequence {

    /**
     *
     * @param {Object} [config] configuration used to build a BootstrapConfiguration instance
     * @param {SDK.Media.PlayerAdapter} PlayerAdapter player adapter to use in sequence
     * @param {NativePlayer} nativePlayer platform native player instance
     * @param {String} [queryContext] GraphQl query context
     * @param {SDK.Content.GraphQlQuery} [graphQlQuery] GraphQlQuery instance
     * @param {SDK.Authentication.AccountClaim} [accountClaim] account claim to use
     * @throws {InvalidArgumentException}
     *
     */
    constructor(config, PlayerAdapter, nativePlayer, queryContext, graphQlQuery, accountClaim) {

        const ctor = `PlaybackCleanupSequence(config, PlayerAdapter, nativePlayer, queryContext,
            graphQlQuery, accountClaim)`;

        const configError = `${ctor} config should be a plain Object`;
        const playerAdapterError = `${ctor} needs PlayerAdapter`;
        const nativePlayerError = `${ctor} needs NativePlayer instance`;
        const queryContextError = `${ctor} queryContext should be a String with length`;
        const graphQlQueryError = `${ctor} needs GraphQlQuery instance`;
        // const accountClaimError = `${ctor} needs AccountClaim instance`;

        const GraphQlQuery = SDK.Content.GraphQlQuery;

        if (Check.assigned(config)) {
            object(config, configError, InvalidArgumentException);
        }

        assigned(PlayerAdapter, playerAdapterError, InvalidArgumentException);
        assigned(nativePlayer, nativePlayerError, InvalidArgumentException);

        if (Check.assigned(queryContext)) {
            nonEmptyString(queryContext, queryContextError, InvalidArgumentException);
        }

        if (Check.assigned(graphQlQuery)) {
            instanceStrict(graphQlQuery, GraphQlQuery, graphQlQueryError, InvalidArgumentException);
        }

        super(config, accountClaim);

        /**
         *
         * @access public
         * @type {SDK.Media.PlayerAdapter}
         *
         */
        this.PlayerAdapter = PlayerAdapter;

        /**
         *
         * @access public
         * @type {NativePlayer}
         *
         */
        this.nativePlayer = nativePlayer;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.queryContext = queryContext;

        /**
         *
         * @access public
         * @type {SDK.Content.GraphQlQuery}
         *
         */
        this.graphQlQuery = graphQlQuery;

    }

    /**
     *
     * @method execute
     * @desc runs the sequence to completion
     * @returns {Promise<SDK.Media.PlayerAdapter>}
     *
     * @example playbackCleanupSequence.execute().then(() => { console.log('success'); });
     *
     */
    execute() {

        const { config, PlayerAdapter, nativePlayer, queryContext, graphQlQuery, accountClaim } = this;

        return new Promise((resolve, reject) => {

            const playbackSequence = new PlaybackSequence(
                config,
                PlayerAdapter,
                nativePlayer,
                queryContext,
                graphQlQuery,
                accountClaim,
                null, // geoProvider
                true // doCleanup
            );

            return playbackSequence.execute().then((playerAdapter) => {

                /** @test SDK.Media.PlayerAdapter */
                assert.ok(playerAdapter.playlistUri.length === 0);
                assert.ok(Object.keys(playerAdapter.boundHandlers).length === 0);

                assert.ok(playerAdapter.listener === null);
                assert.ok(playerAdapter.encryptionKeyProvider === null);
                assert.ok(playerAdapter.unavailableMetric === null);

                return resolve(playerAdapter);

            }).catch((ex) => {

                return reject(ex);

            });

        });

    }

}



// WEBPACK FOOTER //
// ./src/sequences/media/playbackCleanup.js